import React from "react";
import { motion } from "framer-motion";
import { FaGem, FaStar } from "react-icons/fa";
import LiquidGlass from "./LiquidGlass";

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.4, ease: "easeOut" },
  },
};

// Shows the cheapest / most relevant price option on the card
const PriceTag = ({ channel }) => {
  const isFree =
    (!channel.points || channel.points === 0) &&
    (!channel.stars || channel.stars === 0) &&
    (!channel.price || channel.price === 0) &&
    (!channel.tonStars || channel.tonStars === 0);

  if (channel.status !== "active" && channel.status !== "closed" && channel.status !== "paid") {
    return (
      <span className="text-[10px] font-semibold text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
        Coming Soon
      </span>
    );
  }

  if (isFree) {
    return (
      <span className="text-[10px] font-bold text-[#0C8BC4] bg-[#0C8BC4]/10 px-2 py-0.5 rounded-full uppercase tracking-wide">
        Free
      </span>
    );
  }

  if (channel.points > 0) {
    return (
      <div className="flex items-center gap-1">
        <FaGem className="text-amber-500 dark:text-yellow-300 text-[10px]" />
        <span className="text-xs font-semibold text-amber-600 dark:text-yellow-300">
          {channel.points}
          {channel.salePoints > 0 && (
            <span className="line-through text-slate-400 dark:text-slate-500 ml-1 font-normal">
              {channel.salePoints}
            </span>
          )}
        </span>
      </div>
    );
  }

  if (channel.stars > 0) {
    return (
      <div className="flex items-center gap-1">
        <FaStar className="text-amber-500 text-[10px]" />
        <span className="text-xs font-semibold text-amber-600 dark:text-yellow-300">
          {channel.stars}
          {channel.saleStars > 0 && (
            <span className="line-through text-slate-400 ml-1 font-normal">{channel.saleStars}</span>
          )}
        </span>
      </div>
    );
  }

  if (channel.tonStars > 0) {
    return (
      <span className="text-xs font-semibold text-[#0088cc]">{channel.tonStars} TON</span>
    );
  }

  return null;
};

const ChannelCard = ({ channel, onClick }) => {
  const isUnavailable =
    channel.status !== "active" &&
    channel.status !== "closed" &&
    channel.status !== "paid";

  return (
    <motion.div
      key={channel.chatId}
      variants={itemVariants}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.97 }}
      onClick={onClick}
      className={`h-full cursor-pointer ${isUnavailable ? "opacity-70" : ""}`}
    >
      <LiquidGlass
        className={`h-full flex flex-col overflow-hidden rounded-2xl border-2 p-0 transition-colors duration-300 ${
          channel.isSuperFeatured
            ? "border-purple-300 dark:border-purple-400"
          : channel.isFeatured
            ? "border-sky-300 dark:border-sky-400"
            : "border-slate-200 dark:border-slate-700"
        } ${channel.status === "active" || channel.status === "paid" ? "activeChannel" : "inactiveChannel"}`}
        glassColor={
          channel.isSuperFeatured
            ? {
                from: "from-purple-50/90 dark:from-purple-950/90",
                via: "via-white/80 dark:via-slate-900/80",
                to: "to-white/70 dark:to-slate-900/70",
                overlayFrom: "from-purple-400/20",
                overlayVia: "via-fuchsia-400/10",
                overlayTo: "to-pink-400/10",
                glowFrom: "from-white/10",
                glowTo: "to-transparent",
              }
          : channel.isFeatured
            ? {
                from: "from-sky-50/90 dark:from-sky-950/90",
                via: "via-white/80 dark:via-slate-900/80",
                to: "to-white/70 dark:to-slate-900/70",
                overlayFrom: "from-sky-400/20",
                overlayVia: "via-blue-400/10",
                overlayTo: "to-pink-400/10",
                glowFrom: "from-white/10",
                glowTo: "to-transparent",
              }
            : {
                from: "from-white/90 dark:from-slate-900/90",
                via: "via-slate-50/80 dark:via-slate-800/80",
                to: "to-white/70 dark:to-slate-900/70",
                overlayFrom: "from-blue-400/10",
                overlayVia: "via-purple-400/10",
                overlayTo: "to-pink-400/10",
                glowFrom: "from-white/5 dark:from-white/10",
                glowTo: "to-transparent",
              }
        }
      >
        {/* Thumbnail */}
        {channel.thumbnail && (
          <div className="relative w-full h-36 overflow-hidden">
            <img
              src={channel.thumbnail}
              alt={channel.name}
              className={`w-full h-full object-cover ${isUnavailable ? "grayscale" : ""}`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            {channel.isSuperFeatured ? (
              <span className="absolute top-2 right-2 bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md">
                Popular
              </span>
            ) : channel.isFeatured ? (
              <span className="absolute top-2 right-2 bg-sky-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                Featured
              </span>
            ) : null}
          </div>
        )}

        {/* Content */}
        <div className="p-4 flex flex-col flex-grow">
          {/* Title + featured badge (when no thumbnail) */}
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="text-base font-bold text-slate-900 dark:text-white leading-tight">
              {channel.name}
            </h3>
            {!channel.thumbnail && channel.isSuperFeatured ? (
              <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white shadow-md">
                Popular
              </span>
            ) : !channel.thumbnail && channel.isFeatured ? (
              <span className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-500 text-white">
                Featured
              </span>
            ) : null}
          </div>

          {/* Description */}
          {channel.description && (
            <p className="text-slate-500 dark:text-slate-400 text-xs leading-relaxed mb-3 line-clamp-2 flex-grow">
              {channel.description}
            </p>
          )}

          {/* Cast preview */}
          {channel.casting?.length > 0 && (
            <p className="text-slate-400 dark:text-slate-500 text-[10px] mb-2 truncate">
              🎭 {channel.casting.map((c) => c.name).join(" · ")}
            </p>
          )}

          {/* Footer: price + closing indicator */}
          <div className="flex items-center justify-between mt-auto pt-2 border-t border-slate-100 dark:border-slate-700/60">
            <PriceTag channel={channel} />
            {channel.closedDate && channel.status === "active" && (
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
                <span className="text-[10px] text-amber-600 dark:text-amber-400">
                  Until {new Date(channel.closedDate).toLocaleDateString("en-GB")}
                </span>
              </div>
            )}
          </div>
        </div>
      </LiquidGlass>
    </motion.div>
  );
};

export default ChannelCard;
